'use client'

import { useState, useRef, useLayoutEffect, useCallback } from 'react'
import Link from 'next/link'
import DrMeenaSection from '@/components/team/DrMeenaSection'
import ClinicalTeamSection from '@/components/team/clinicalTeamSection'
import TeamPhilosophy from '@/components/team/teamPhilosophy'
import BioModal from '@/components/team/teamMemberModal'
import type { TeamMember } from '@/types/doctor'
import TeamAssessments from '@/components/team/teamAssessments'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const ProfessionalTeamPage: React.FC = () => {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)
  const titleRef = useRef<HTMLHeadingElement | null>(null)
  const subtitleRef = useRef<HTMLParagraphElement | null>(null)

  const handleViewBio = useCallback((member: TeamMember) => {
    setSelectedMember(member)
  }, [])

  const handleCloseModal = useCallback(() => {
    setSelectedMember(null)
  }, [])

  useLayoutEffect(() => {
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    if (prefersReduced) return

    let scrollTriggerInstance: ScrollTrigger | null = null

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ paused: true })

      if (titleRef.current) {
        tl.fromTo(
          titleRef.current,
          { y: 16, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' },
          0,
        )
      }

      if (subtitleRef.current) {
        tl.fromTo(
          subtitleRef.current,
          { y: 12, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' },
          0.1,
        )
      }

      scrollTriggerInstance = ScrollTrigger.create({
        trigger: titleRef.current,
        start: 'top 85%',
        once: true,
        onEnter: () => tl.play(),
      })

      requestAnimationFrame(() => {
        ScrollTrigger.refresh()
      })
    })

    return () => {
      ctx.revert()
      scrollTriggerInstance?.kill()
    }
  }, [])

  return (
    <div className="min-h-screen pt-16 sm:pt-20 bg-white">
      {/* Page Header */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-10 sm:pt-16 md:pt-20 lg:pt-24">
          <div className="text-center mb-8 sm:mb-10 md:mb-14">
            <h1
              ref={titleRef}
              className="font-[var(--font-league-spartan,_inherit)] text-[28px] sm:text-4xl md:text-5xl lg:text-[56px] xl:text-[64px] leading-[1.1] tracking-tight text-black"
            >
              Meet our Expert Team
            </h1>
            <p
              ref={subtitleRef}
              className="mt-3 sm:mt-4 md:mt-5 max-w-3xl lg:max-w-4xl mx-auto text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed text-gray-600 px-1 sm:px-0"
            >
              Led by Dr. Meena Gnanashekharan, our multidisciplinary team brings
              decades of experience in evidence-based mental healthcare to
              support your journey to wellness.
            </p>
          </div>
        </div>
      </section>

      {/* Dr. Meena Section */}
      <DrMeenaSection onViewBio={handleViewBio} />

      {/* Clinical Team Section */}
      <ClinicalTeamSection onViewBio={handleViewBio} />

      {/* Team Assessments */}
      <TeamAssessments />

      {/* Team Philosophy Section */}
      <TeamPhilosophy />

      {/* Practical next step after meeting the team */}
      <section
        aria-labelledby="team-book-heading"
        className="mx-auto max-w-4xl px-5 py-10 text-center sm:px-6 sm:py-14"
      >
        <h2
          id="team-book-heading"
          className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl"
        >
          Ready to meet the team in person?
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-gray-600 sm:text-base">
          <Link
            href="/book"
            className="font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
          >
            Book an appointment
          </Link>{' '}
          or{' '}
          <Link
            href="/about"
            className="font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
          >
            find our clinic and opening hours
          </Link>
          .
        </p>
      </section>

      {/* Bio Modal */}
      <BioModal member={selectedMember} onClose={handleCloseModal} />
    </div>
  )
}

export default ProfessionalTeamPage
